import styled, { css, keyframes } from "styled-components";
import React, { ReactNode } from "react";
import { Loader2 } from "lucide-react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger";
  icon?: ReactNode;
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  $isActive?: boolean;
  className?: string;
  $backgroundColor?: string;
}

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const StyledButton = styled.button<{
  $variant: "primary" | "secondary" | "danger";
  $isActive?: boolean;
  $backgroundColor?: string;
  $fullWidth?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-4);
  border-radius: 50px;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-size: 0.9rem;
  width: ${props => props.$fullWidth ? '100%' : 'auto'};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }

  &:hover::before {
    transform: translateX(100%);
  }

  ${({ $variant, $isActive, $backgroundColor }) =>
    $variant === "primary"
      ? css`
          background: ${$backgroundColor || ($isActive ? "#FFD700" : "var(--color-primary)")};
          color: #000;
          border: none;

          &:hover {
            background: ${$backgroundColor || "#FFD700"};
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(255, 215, 0, 0.4);
          }

          &:disabled {
            background: #4a4a4a;
            color: #8a8a8a;
            cursor: not-allowed;
            box-shadow: none;
            transform: none;
          }

          ${$isActive &&
          css`
            background: #FFD700;
            box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
          `}
        `
      : $variant === "danger"
      ? css`
          background: #FFD700;
          color: #000;
          border: none;

          &:hover {
            background: #E6C200;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(255, 215, 0, 0.4);
          }

          &:disabled {
            background: #8a8a8a;
            color: #4a4a4a;
            cursor: not-allowed;
            box-shadow: none;
            transform: none;
          }
        `
      : css`
          background: transparent;
          color: var(--color-text-primary);
          border: 2px solid var(--color-text-primary);

          &:hover {
            background: rgba(255, 255, 255, 0.1);
          }

          &:disabled {
            border-color: #4a4a4a;
            color: #8a8a8a;
            cursor: not-allowed;
          }

          ${$isActive &&
          css`
            background: rgba(255, 255, 255, 0.1);
          `}
        `}
`;

const LoadingSpinner = styled(Loader2)`
  animation: ${rotate} 1s linear infinite;
`;

export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  icon,
  children,
  onClick,
  type = "button",
  disabled = false,
  loading = false,
  fullWidth = false,
  $isActive = false,
  className,
  $backgroundColor,
}) => {
  return (
    <StyledButton
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      $variant={variant}
      $isActive={$isActive}
      className={className}
      $backgroundColor={$backgroundColor}
      $fullWidth={fullWidth}
    >
      {loading ? (
        <LoadingSpinner size={20} />
      ) : (
        <>
          {icon}
          {children}
        </>
      )}
    </StyledButton>
  );
};
